import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [phone, setPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { phone, newPassword });
      toast.success("Password changed successfully");
      nav("/login");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-base flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 text-emerald-muted hover:text-gold transition">
          <Sparkles className="w-4 h-4 text-gold" />
          <span className="font-heading text-2xl text-white">SOS (Stan Of Style)</span>
        </Link>
        <div className="overline mb-3">Account Recovery</div>
        <h1 className="font-heading text-4xl text-white mb-3">Reset Password</h1>
        <p className="text-emerald-muted mb-8 text-sm">Enter your phone number and set a new password.</p>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
          <div>
            <Label htmlFor="phone" className="text-emerald-muted text-xs uppercase tracking-widest">Phone</Label>
            <Input id="phone" data-testid="reset-phone-input" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="enter your phone number" />
          </div>
          <div>
            <Label htmlFor="new-password" className="text-emerald-muted text-xs uppercase tracking-widest">New Password</Label>
            <Input id="new-password" data-testid="reset-new-password-input" type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="At least 6 characters" />
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-emerald-muted text-xs uppercase tracking-widest">Confirm Password</Label>
            <Input id="confirm-password" data-testid="reset-confirm-password-input" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-2 bg-emerald-surface border-gold-subtle text-white focus:border-gold h-12" placeholder="Re-enter password" />
          </div>
          <button type="submit" disabled={loading} data-testid="reset-submit-btn"
            className="gold-button w-full h-12 rounded-md disabled:opacity-60">{loading ? "Resetting…" : "Reset Password"}</button>
        </form>

        <p className="text-sm text-emerald-muted mt-8">
          Remember your password? <Link to="/login" className="text-gold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
